import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Paper,
  Chip,
  Stack,
  SelectChangeEvent,
} from '@mui/material';
import {
  Description as ApiIcon,
  Security as SecurityIcon,
  Code as CodeIcon,
  Assessment as AnalyticsIcon,
} from '@mui/icons-material';

interface ServiceConfig {
  name: string;
  description: string;
  port: number;
  docsUrl: string;
  apiUrl: string;
  color: string;
  icon: React.ReactNode;
  status: 'active' | 'maintenance';
  endpoints: number;
}

// Get base URL from environment or use current origin
const getBaseUrl = () => {
  // In production, use the current origin (e.g., https://testingdb.ev91riders.com)
  // In development, use localhost:8000
  const isDev = import.meta.env.DEV;
  const envBaseUrl = import.meta.env.VITE_API_GATEWAY_URL;

  if (envBaseUrl) {
    return envBaseUrl;
  }

  if (isDev) {
    return 'http://localhost:8000';
  }

  // In production, use current origin without port
  return window.location.origin;
};

const baseUrl = getBaseUrl();

const services: Record<string, ServiceConfig> = {
  'auth-service': {
    name: 'Auth Service',
    description: 'Authentication, authorization, user management, RBAC, employees, departments, teams, and roles',
    port: 4001,
    docsUrl: `${baseUrl}/api/auth-service/api/docs`,
    apiUrl: `${baseUrl}/api/auth`,
    color: '#2196F3',
    icon: <SecurityIcon />,
    status: 'active',
    endpoints: 55,
  },
  'rider-service': {
    name: 'Rider Service',
    description: 'Rider registration, KYC, orders, payments, security deposits, rentals, vendors, referrals, and bulk operations',
    port: 4005,
    docsUrl: `${baseUrl}/api/rider-service/api/docs`,
    apiUrl: `${baseUrl}/api/riders`,
    color: '#4CAF50',
    icon: <ApiIcon />,
    status: 'active',
    endpoints: 100,
  },
  'vehicle-service': {
    name: 'Vehicle Service',
    description: 'Vehicle inventory, batteries, chargers, cities, hubs, OEMs, models, service records, damage tracking, and analytics',
    port: 8001,
    docsUrl: `${baseUrl}/api/vehicle-service/api/docs`,
    apiUrl: `${baseUrl}/api/vehicles`,
    color: '#FF9800',
    icon: <CodeIcon />,
    status: 'active',
    endpoints: 80,
  },
  'spare-parts-service': {
    name: 'Spare Parts Service',
    description: 'Spare parts catalog, inventory management, suppliers, purchase orders, outward flow, and analytics',
    port: 4006,
    docsUrl: `${baseUrl}/api/spare-parts-service/api/docs`,
    apiUrl: `${baseUrl}/api/spare-parts`,
    color: '#9C27B0',
    icon: <AnalyticsIcon />,
    status: 'active',
    endpoints: 60,
  },
  'client-store-service': {
    name: 'Client Store Service',
    description: 'Client management, store operations, rider-client mapping, and earnings tracking',
    port: 3006,
    docsUrl: `${baseUrl}/api/client-store-service/api/docs`,
    apiUrl: `${baseUrl}/api/clients`,
    color: '#00BCD4',
    icon: <ApiIcon />,
    status: 'active',
    endpoints: 35,
  },
  'war-room-service': {
    name: 'War Room Service',
    description: 'Real-time vehicle monitoring, alerts, city statistics, and live tracking with Ops Pod integration',
    port: 4020,
    docsUrl: 'http://localhost:4020/docs',
    apiUrl: 'http://localhost:4020',
    color: '#E91E63',
    icon: <AnalyticsIcon />,
    status: 'active',
    endpoints: 4,
  },
  'api-gateway': {
    name: 'API Gateway',
    description: 'Central gateway routing requests to all microservices. Unified access point for admin portal and mobile apps',
    port: 8000,
    docsUrl: `${baseUrl}/docs`,
    apiUrl: baseUrl,
    color: '#607D8B',
    icon: <SecurityIcon />,
    status: 'active',
    endpoints: 334,
  },
};

const APIDocumentation: React.FC = () => {
  const [selectedService, setSelectedService] = useState<string>('auth-service');

  const handleServiceChange = (event: SelectChangeEvent<string>) => {
    setSelectedService(event.target.value);
  };

  const currentService = services[selectedService];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          API Documentation
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Explore comprehensive API documentation for all EV91 Platform services. Select a service below to view its interactive Swagger UI documentation.
        </Typography>
      </Box>

      {/* Service Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: '#f5f5f5',
            }}
          >
            <Typography variant="h4" fontWeight="bold" color="primary">
              {Object.keys(services).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Services
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: '#f5f5f5',
            }}
          >
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {Object.values(services).reduce((sum, s) => sum + s.endpoints, 0)}+
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Endpoints
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: '#f5f5f5',
            }}
          >
            <Typography variant="h4" fontWeight="bold" color="success.main">
              ✓
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All Services Active
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: '#f5f5f5',
            }}
          >
            <Typography variant="h4" fontWeight="bold" color="primary">
              3.0.0
            </Typography>
            <Typography variant="body2" color="text.secondary">
              OpenAPI Version
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Service Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {Object.entries(services).map(([key, service]) => (
          <Grid item xs={12} sm={6} md={4} key={key}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                border: selectedService === key ? `3px solid ${service.color}` : '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                },
              }}
              onClick={() => setSelectedService(key)}
            >
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Box sx={{ color: service.color, display: 'flex' }}>
                    {service.icon}
                  </Box>
                  <Typography variant="h6" fontWeight="bold">
                    {service.name}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {service.description}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={`Port: ${service.port}`}
                    size="small"
                    sx={{ bgcolor: service.color, color: 'white' }}
                  />
                  <Chip
                    label={`${service.endpoints}+ APIs`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={service.status === 'active' ? 'Active' : 'Maintenance'}
                    size="small"
                    color={service.status === 'active' ? 'success' : 'warning'}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Service Selector and Swagger UI */}
      <Card>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="service-select-label">Select Service</InputLabel>
              <Select
                labelId="service-select-label"
                id="service-select"
                value={selectedService}
                label="Select Service"
                onChange={handleServiceChange}
              >
                {Object.entries(services).map(([key, service]) => (
                  <MenuItem key={key} value={key}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ color: service.color, display: 'flex' }}>
                        {service.icon}
                      </Box>
                      <Box>
                        <Typography variant="body1">{service.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Port: {service.port} | {service.endpoints}+ endpoints
                        </Typography>
                      </Box>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {currentService && (
            <Box>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: '#f5f5f5',
                  borderLeft: `4px solid ${currentService.color}`,
                }}
              >
                <Stack spacing={1}>
                  <Typography variant="h6" fontWeight="bold">
                    {currentService.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentService.description}
                  </Typography>
                  <Stack direction="row" spacing={2} mt={1}>
                    <Chip
                      label={`Base URL: ${currentService.apiUrl}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`Documentation: ${currentService.docsUrl}`}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                </Stack>
              </Paper>

              {/* Swagger UI Iframe */}
              <Box
                sx={{
                  width: '100%',
                  height: '800px',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <iframe
                  src={currentService.docsUrl}
                  title={`${currentService.name} API Documentation`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                />
              </Box>

              {/* Quick Links */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mt: 2,
                  bgcolor: '#f5f5f5',
                }}
              >
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Quick Links
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <Chip
                    label="Open in New Tab"
                    component="a"
                    href={currentService.docsUrl}
                    target="_blank"
                    clickable
                    color="primary"
                  />
                  <Chip
                    label="OpenAPI JSON Spec"
                    component="a"
                    href={`${currentService.apiUrl}/openapi.json`}
                    target="_blank"
                    clickable
                    variant="outlined"
                  />
                  <Chip
                    label="Health Check"
                    component="a"
                    href={`${currentService.apiUrl}/health`}
                    target="_blank"
                    clickable
                    variant="outlined"
                  />
                </Stack>
              </Paper>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            📚 How to Use API Documentation
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5', height: '100%' }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  1. Authenticate
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click the "Authorize" button in Swagger UI and enter your Bearer token to test authenticated endpoints.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5', height: '100%' }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  2. Explore Endpoints
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Browse through organized API categories. Click on any endpoint to see detailed request/response schemas.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5', height: '100%' }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  3. Test APIs
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click "Try it out" on any endpoint, fill in parameters, and execute to test the API directly from the docs.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default APIDocumentation;
